import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sparkles } from 'lucide-react';

interface SocialLinkProps {
  href: string;
  iconSrc: string;
  label: string;
  color: string;
  bgColor: string;
  index: number;
}

const SocialLink: React.FC<SocialLinkProps> = ({ href, iconSrc, label, index }) => {
  return (
    <motion.div
      initial={{ x: -100, opacity: 0, scale: 0.9 }}
      animate={{ x: 0, opacity: 1, scale: 1 }}
      exit={{ x: -100, opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 350, damping: 20, delay: index * 0.08 }}
      whileHover={{ x: 12, scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="mb-2.5 last:mb-0"
    >
      <motion.a
        href={href}
        target={href.startsWith('mailto:') || href.startsWith('tel:') ? '_self' : '_blank'}
        rel="noopener noreferrer"
        className="group relative flex items-center rounded-r-2xl pl-3 pr-5 py-2.5 shadow-sm hover:shadow-md transition-all duration-300 min-w-[190px]"
        style={{
          background: 'rgba(255, 255, 255, 0.8)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          borderLeft: 'none'
        }}
      >
        <motion.div 
          className="relative w-9 h-9 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300 p-2 shadow-sm"
          style={{ background: 'rgba(0, 0, 0, 0.03)' }}
          whileHover={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 0.5 }}
        >
          <img 
            src={iconSrc} 
            alt={label} 
            className="w-full h-full object-contain relative z-10"
          />
        </motion.div>
        
        <span className="relative z-10 text-slate-800 font-medium text-sm tracking-wide" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          {label}
        </span>
      </motion.a>
    </motion.div>
  );
};

const SocialSidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        const target = event.target as HTMLElement;
        const sidebarElement = document.getElementById('social-sidebar-container');
        if (sidebarElement && !sidebarElement.contains(target)) {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const socialLinks = [
    { href: 'https://www.instagram.com/amitadhikary02/', iconSrc: '/assets/images/Contacts/instagram.png', label: 'Instagram', color: '', bgColor: '' },
    { href: 'https://www.facebook.com/amitadhikary02', iconSrc: '/assets/images/Contacts/facebook.png', label: 'Facebook', color: '', bgColor: '' },
    { href: 'https://wa.me/+918101029684', iconSrc: '/assets/images/Contacts/whatsapp.png', label: 'WhatsApp', color: '', bgColor: '' },
    { href: 'https://www.linkedin.com/in/amitadhikary02/', iconSrc: '/assets/images/Contacts/linkedin.png', label: 'LinkedIn', color: '', bgColor: '' },
    { href: 'mailto:amitadhikary59684@gmail.com', iconSrc: '/assets/images/Contacts/email.png', label: 'Email Me', color: '', bgColor: '' },
    { href: 'https://github.com/amitadhikary02', iconSrc: '/assets/images/Contacts/github.png', label: 'GitHub', color: '', bgColor: '' },
    { href: 'https://leetcode.com/u/amitadhikary02/', iconSrc: '/assets/images/Contacts/leetcode.png', label: 'LeetCode', color: '', bgColor: '' },
    { href: 'https://www.hackerrank.com/profile/amitadhikary02', iconSrc: '/assets/images/Contacts/hackerrank.png', label: 'HackerRank', color: '', bgColor: '' }
  ];

  const sidebarContent = (
    <div 
      id="social-sidebar-container"
      className="fixed left-0 bottom-24 lg:top-1/2 lg:bottom-auto lg:transform lg:-translate-y-1/2" 
      style={{ 
        zIndex: 2147483640,
        pointerEvents: 'auto',
      }}
    >
      <div className="flex items-center">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, x: -20, width: 0 }}
              animate={{ opacity: 1, x: 0, width: 'auto' }}
              exit={{ opacity: 0, x: -20, width: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="mr-2 overflow-hidden"
            >
              <div className="relative liquid-glass-card rounded-r-3xl py-3 px-2 shadow-2xl" style={{ borderLeft: 'none' }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mb-3 px-2"
                >
                  <div className="flex items-center justify-center space-x-2 bg-sky-50 rounded-xl py-2 px-3 border border-sky-100">
                    <Sparkles size={14} className="text-sky-500" />
                    <span className="text-xs font-bold text-slate-800 font-heading">Connect With Me</span>
                  </div>
                </motion.div>

                <div className="space-y-2">
                  {socialLinks.map((link, index) => (
                    <SocialLink
                      key={link.label}
                      href={link.href}
                      iconSrc={link.iconSrc}
                      label={link.label}
                      color={link.color}
                      bgColor={link.bgColor}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
          
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-14 h-14 rounded-r-2xl flex items-center justify-center shadow-lg transition-all duration-300 liquid-glass-card"
          whileHover={{ scale: 1.08, x: 2 }}
          whileTap={{ scale: 0.95 }}
          style={{
            borderTopLeftRadius: '0',
            borderBottomLeftRadius: '0',
            borderLeft: 'none'
          }}
        >
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="relative z-10"
          >
            {isOpen ? (
              <X size={24} className="text-slate-700" strokeWidth={2.5} />
            ) : (
              <Menu size={24} className="text-slate-700" strokeWidth={2.5} />
            )}
          </motion.div>
        </motion.button>
      </div>

      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, x: -10, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -10, scale: 0.9 }}
            className="absolute left-full top-1/2 transform -translate-y-1/2 ml-4 hidden lg:block pointer-events-none"
          >
            <div className="liquid-glass-card px-4 py-2 rounded-xl flex items-center gap-2">
              <Sparkles size={12} className="text-sky-500" />
              <span className="text-xs font-semibold text-slate-700 font-heading whitespace-nowrap">Social Links</span>
            </div>
            <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-y-[6px] border-y-transparent border-r-[8px] border-r-white/60"></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (!mounted) return null;
  return ReactDOM.createPortal(sidebarContent, document.body);
};

export default SocialSidebar;